import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertQuestionMappings,
  convertNotRequired,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = 'all'
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 以下皆為 1-based 寫法，convertVisibleIf 會自動 -1 還原為 0-based。
  // JSON 前 56 題為訪員填寫的名冊/地址/障別欄位（A~E2），問卷本文自 q57 起。
  // PDF 題號 ↔ JSON 題號：1=q58、2a/2b=q59/q60、3=q61、4=q62、5=q63、6=q64、
  //   7-1/7-2=q65/q66、8=q67、8a/8b=q68/q69、9=q70、9a-9d=q71-q74、10a/10b=q75/q76、
  //   11=q77、12=q78、12a=q79、13=q81、14-20=q82-q91（18a=q87、19=q88、19-1=q89、19a=q90）、
  //   21-27=q92-q100、28=q101、29=q102、30=q104、31=q105、32=q106、33-1~3=q107-q109、
  //   34=q110、35A-E=q111-q120（每組「有沒有搭乘」+「困難」）、36/36a=q121/q122、
  //   37/37a=q123/q124、38/39=q126/q127、40=q128、40a=q129、41=q130、41O=q131、
  //   41a1=q132（繼承 41 的勾選項）、41a2=q133（完整 22 項代碼，跳題判斷用這題）、
  //   42=q134-q141、43-51=q142-q157、52=q159、53=q160-q162、54=q163、55A-D=q164-q167、
  //   56=q168、57=q169、58=q170、59/59a=q171/q172、60=q174、60a=q175、60b=q176、
  //   60b-2=q177、60c=q178、61=q179、62=q180、63=q182、63a/63b=q183/q184、64=q185、
  //   64a/64b=q186/q187、65/65a=q188/q189、66=q190、66a/66b=q191/q192、67/67a=q193/q194、
  //   68=q195、68a=q196、68b=q197、68bO=q198、68c=q199、69=q200、70=q201、
  //   70a-70c=q202-q204、71=q205、72/72a=q206/q207、73-1=q209、73-2=q210、
  //   73-2a~d=q211-q214、74/74a=q215/q216、75-1=q217、75-2=q218、75-2a~d=q219-q222、
  //   76/76a=q223/q224、77=q225、78=q226、79=q227、80=q228、81=q229、82=q230、
  //   82a-82c=q231-q233、F=q234、F1-F3=q235-q237。

  // A題 (2)與名冊地址不同 → 才問 A1-A3 戶籍地址題組(q2-q25)
  const registeredDiffers = '{q1} = 2'
  // B題 (2)與戶籍地址不同 → 才問 B1-B3 居住地址題組(q27-q50)
  const livesElsewhere = '{q26} = 2'

  // E1 舊制障礙類別(q55) 第9項＝植物人：第12題、陸章工作現況免填答，且第72題後結束訪查
  const notPlantPerson = '{q55} <> 9'
  // 2a題 出生年(q59)：第1項是民國0年(115歲)、第116項是民國115年(0歲)，
  // 故「足歲年齡 = 116 - 選項序號」，年齡門檻一律換算成序號區間。
  const isAge3AndUp = '{q59} <= 113' // 3歲以上（未滿3歲＝114-116項）
  const isAge3To5 = '({q59} >= 111 and {q59} <= 113)' // 3歲至未滿6歲
  const isAge6AndUp = '{q59} <= 110' // 6歲以上
  const isAge12AndUp = '{q59} <= 104' // 12歲及以上
  const isAge15AndUp = '{q59} <= 101' // 15歲以上（工作現況）
  const isAge25To74 = '({q59} >= 42 and {q59} <= 91)' // 25歲至未滿75歲（癌症篩檢）
  // 76題 限民國93年1月13日前(含當日)出生；JSON 只到「月」，93年1月整月視為符合
  const bornBeforeElectionDay = '(({q59} <= 93) or ({q59} = 94 and {q60} = 1))'
  // 13題 居住地點：(1)一般住宅續問 14-20 題；(2)住宿式機構續問 21-27 題
  const livesAtHome = '{q81} = 1'
  const livesInFacility = '{q81} = 2'
  // 30題 (5)都沒有外出者，31-33 題免答
  const goesOut = '({q104} = 1 or {q104} = 2 or {q104} = 3 or {q104} = 4)'
  // 40題 (1)可以獨立自我照顧 → 跳問 52 題，故 40a-51 題都要「非(1)」
  const needsCare = '({q128} = 2 or {q128} = 3)'
  // 43-51 題另限「住一般住宅者填答」
  const homeServiceSection = '(' + livesAtHome + ') and ' + needsCare
  // 陸章 60-62 題：15歲以下、障礙類別為植物人者免填答
  const workSection = '(' + notPlantPerson + ') and (' + isAge15AndUp + ')'
  // 19題 12 項設施任一選「(2)需要」→ 續問 19a
  const needsAnyFacility =
    '({q88.1} = 2) or ({q88.2} = 2) or ({q88.3} = 2) or ({q88.4} = 2) or ' +
    '({q88.5} = 2) or ({q88.6} = 2) or ({q88.7} = 2) or ({q88.8} = 2) or ' +
    '({q88.9} = 2) or ({q88.10} = 2) or ({q88.11} = 2) or ({q88.12} = 2)'
  // 41a2(q133) 填(1)~(12)家人代碼者續答 42 題；填(13)~(22)者跳答 43 題
  // 判斷用 41a2 而非繼承題 41a1(q132)：繼承來的 value 會被重新編號，索引不可靠
  const mainCarerIsFamily = '({q133} >= 1 and {q133} <= 12)'
  // 74題 73-1 四項休閒活動全部「(2)沒有」者不用回答（任一項有參加才問）
  const joinedAnyLeisure =
    '({q209.1} = 1) or ({q209.2} = 1) or ({q209.3} = 1) or ({q209.4} = 1)'

  const visibleIfConditionsInit = convertVisibleIf({
    // A1-A3. 限 A 題選(2)與名冊地址不同者填答
    q2: registeredDiffers,
    q25: registeredDiffers,
    // A2. 戶籍地址_鄉鎮市區：22 題各對應一個縣市，只顯示 A1(q2) 所選縣市的那一題
    q3: registeredDiffers + ' and ({q2} = 1)', // 新北市
    q4: registeredDiffers + ' and ({q2} = 2)', // 臺北市
    q5: registeredDiffers + ' and ({q2} = 3)', // 基隆市
    q6: registeredDiffers + ' and ({q2} = 4)', // 桃園市
    q7: registeredDiffers + ' and ({q2} = 5)', // 新竹市
    q8: registeredDiffers + ' and ({q2} = 6)', // 新竹縣
    q9: registeredDiffers + ' and ({q2} = 7)', // 苗栗縣
    q10: registeredDiffers + ' and ({q2} = 8)', // 臺中市
    q11: registeredDiffers + ' and ({q2} = 9)', // 彰化縣
    q12: registeredDiffers + ' and ({q2} = 10)', // 南投縣
    q13: registeredDiffers + ' and ({q2} = 11)', // 雲林縣
    q14: registeredDiffers + ' and ({q2} = 12)', // 嘉義市
    q15: registeredDiffers + ' and ({q2} = 13)', // 嘉義縣
    q16: registeredDiffers + ' and ({q2} = 14)', // 臺南市
    q17: registeredDiffers + ' and ({q2} = 15)', // 高雄市
    q18: registeredDiffers + ' and ({q2} = 16)', // 屏東縣
    q19: registeredDiffers + ' and ({q2} = 17)', // 宜蘭縣
    q20: registeredDiffers + ' and ({q2} = 18)', // 花蓮縣
    q21: registeredDiffers + ' and ({q2} = 19)', // 臺東縣
    q22: registeredDiffers + ' and ({q2} = 20)', // 澎湖縣
    q23: registeredDiffers + ' and ({q2} = 21)', // 金門縣
    q24: registeredDiffers + ' and ({q2} = 22)', // 連江縣
    // B1-B3. 限 B 題選(2)與戶籍地址不同者填答
    q27: livesElsewhere,
    q50: livesElsewhere,
    // B2. 居住地址_鄉鎮市區：同理，對應 B1(q27) 所選縣市，並限 B 題選(2)
    q28: livesElsewhere + ' and ({q27} = 1)', // 新北市
    q29: livesElsewhere + ' and ({q27} = 2)', // 臺北市
    q30: livesElsewhere + ' and ({q27} = 3)', // 基隆市
    q31: livesElsewhere + ' and ({q27} = 4)', // 桃園市
    q32: livesElsewhere + ' and ({q27} = 5)', // 新竹市
    q33: livesElsewhere + ' and ({q27} = 6)', // 新竹縣
    q34: livesElsewhere + ' and ({q27} = 7)', // 苗栗縣
    q35: livesElsewhere + ' and ({q27} = 8)', // 臺中市
    q36: livesElsewhere + ' and ({q27} = 9)', // 彰化縣
    q37: livesElsewhere + ' and ({q27} = 10)', // 南投縣
    q38: livesElsewhere + ' and ({q27} = 11)', // 雲林縣
    q39: livesElsewhere + ' and ({q27} = 12)', // 嘉義市
    q40: livesElsewhere + ' and ({q27} = 13)', // 嘉義縣
    q41: livesElsewhere + ' and ({q27} = 14)', // 臺南市
    q42: livesElsewhere + ' and ({q27} = 15)', // 高雄市
    q43: livesElsewhere + ' and ({q27} = 16)', // 屏東縣
    q44: livesElsewhere + ' and ({q27} = 17)', // 宜蘭縣
    q45: livesElsewhere + ' and ({q27} = 18)', // 花蓮縣
    q46: livesElsewhere + ' and ({q27} = 19)', // 臺東縣
    q47: livesElsewhere + ' and ({q27} = 20)', // 澎湖縣
    q48: livesElsewhere + ' and ({q27} = 21)', // 金門縣
    q49: livesElsewhere + ' and ({q27} = 22)', // 連江縣
    // 7. 未滿3歲不用填答；3歲至未滿6歲填學齡前兩項(q65)，6歲以上填(3)~(10)(q66)
    q65: isAge3To5,
    q66: isAge6AndUp,
    // 8. (2)有配偶/(3)與人同居 → 續問 8a、8b；(1)未婚(4)離婚(5)分居(6)喪偶跳問第 9 題
    q68: '{q67} = 2 or {q67} = 3',
    q69: '{q67} = 2 or {q67} = 3',
    // 9. (2)有子女 → 續問 9a-9d 四個人數欄
    q71: '{q70} = 2',
    q72: '{q70} = 2',
    q73: '{q70} = 2',
    q74: '{q70} = 2',
    // 12. 3歲以下、障礙類別為植物人者免填答，跳問第 13 題
    //     （PDF 註記寫「跳問11題」為筆誤，PM 確認應為第 13 題）
    q78: '(' + notPlantPerson + ') and (' + isAge3AndUp + ')',
    q79: '(' + notPlantPerson + ') and (' + isAge3AndUp + ')',
    // 14-20. 限 13 題選(1)一般住宅
    q82: livesAtHome,
    q83: livesAtHome,
    q84: livesAtHome,
    q85: livesAtHome,
    q86: livesAtHome,
    // 18a. 18 題非「(1)獨居」者才問同住人員；第 11 項是 o_type 6，值為 "other"
    q87:
      '(' +
      livesAtHome +
      ') and (({q86} >= 2 and {q86} <= 10) or ({q86} = "other"))',
    q88: livesAtHome,
    // 19-1. 19 題第 12 列「其他」需要時，才問是哪種設施
    q89: '(' + livesAtHome + ') and ({q88.12} = 2)',
    // 19a. 逐列對應 19 題：該列選「(2)需要」，19a 才出現同一列
    q90: '(' + livesAtHome + ') and (' + needsAnyFacility + ')',
    'q90.row1': '{q88.1} = 2',
    'q90.row2': '{q88.2} = 2',
    'q90.row3': '{q88.3} = 2',
    'q90.row4': '{q88.4} = 2',
    'q90.row5': '{q88.5} = 2',
    'q90.row6': '{q88.6} = 2',
    'q90.row7': '{q88.7} = 2',
    'q90.row8': '{q88.8} = 2',
    'q90.row9': '{q88.9} = 2',
    'q90.row10': '{q88.10} = 2',
    'q90.row11': '{q88.11} = 2',
    'q90.row12': '{q88.12} = 2',
    q91: livesAtHome,
    // 21-27. 限 13 題選(2)住宿式機構
    q92: livesInFacility,
    q93: livesInFacility,
    q94: livesInFacility,
    // 23. (1)沒有家人或親屬 → 跳問第 25 題；(2)有 → 續問 23a、24
    q95: '(' + livesInFacility + ') and ({q94} = 2)',
    q96: '(' + livesInFacility + ') and ({q94} = 2)',
    q97: livesInFacility,
    q98: livesInFacility,
    q99: livesInFacility,
    q100: livesInFacility,
    // 31-33. 30 題選(5)都沒有外出者免答
    q105: goesOut,
    q106: goesOut,
    q107: goesOut,
    // 33-1 選第16項「(95)沒有使用交通工具」→ 33-2、33-3 都不顯示
    q108: goesOut + ' and ({q107} <> 16)',
    // 33-2 選第16項「(95)沒有次要選項」→ 33-3 不顯示
    q109: goesOut + ' and ({q107} <> 16) and ({q108} <> 16)',
    // 34. 限 30 題選(4)很少外出或(5)都沒有外出
    q110: '{q104} = 4 or {q104} = 5',
    // 35A-E. 各組先問「(01)有搭乘」，有搭乘才問困難題
    q112: '{q111} = 1',
    q114: '{q113} = 1',
    q116: '{q115} = 1',
    q118: '{q117} = 1',
    q120: '{q119} = 1',
    // 36. (1)有使用通用計程車 → 續問 36a
    q122: '{q121} = 1',
    // 37. (1)有申請復康巴士 → 續問 37a
    q124: '{q123} = 1',
    // 38、39. 未滿6歲者免答，跳問第 40 題
    q126: isAge6AndUp,
    q127: isAge6AndUp,
    // 40. (1)可以獨立自我照顧 → 跳問第 52 題
    q129: needsCare,
    q130: needsCare,
    // 41O. 41 題勾選(12)其他親戚 → 續問說明（該選項 o_type 為 0，用數字索引）
    q131: needsCare + ' and ({q130} anyof [12])',
    q132: needsCare,
    q133: needsCare,
    // 42. 41a 填(1)~(12)家人代碼者續答 42 題；填(13)~(22)者跳答 43 題
    q134: needsCare + ' and ' + mainCarerIsFamily,
    q135: needsCare + ' and ' + mainCarerIsFamily,
    q136: needsCare + ' and ' + mainCarerIsFamily,
    q137: needsCare + ' and ' + mainCarerIsFamily,
    q138: needsCare + ' and ' + mainCarerIsFamily,
    q139: needsCare + ' and ' + mainCarerIsFamily,
    q140: needsCare + ' and ' + mainCarerIsFamily,
    q141: needsCare + ' and ' + mainCarerIsFamily,
    // 43-51. 住一般住宅者填答，未住一般住宅者跳問第 52 題
    q142: homeServiceSection,
    // 43. (1)不需要居家服務 → 跳問第 46 題（43a-43d、44、45 皆不問）
    q143: homeServiceSection + ' and ({q142} = 2)',
    q144: homeServiceSection + ' and ({q142} = 2)',
    q145: homeServiceSection + ' and ({q142} = 2)',
    q146: homeServiceSection + ' and ({q142} = 2)',
    q147: homeServiceSection + ' and ({q142} = 2)',
    // 44. (1)有申請且符合需要 → 跳問第 46 題，故第 45 題限(2)(3)
    q148:
      homeServiceSection + ' and ({q142} = 2) and ({q147} = 2 or {q147} = 3)',
    q149: homeServiceSection,
    // 46. (1)不需要日間服務 → 跳問第 49 題（46a-46c、47、48 皆不問）
    q150: homeServiceSection + ' and ({q149} = 2)',
    q151: homeServiceSection + ' and ({q149} = 2)',
    q152: homeServiceSection + ' and ({q149} = 2)',
    q153: homeServiceSection + ' and ({q149} = 2)',
    // 47. (1)有申請且符合需要 → 跳問第 49 題，故第 48 題限(2)(3)
    q154:
      homeServiceSection + ' and ({q149} = 2) and ({q153} = 2 or {q153} = 3)',
    q155: homeServiceSection,
    // 49. (1)有申請且符合需要 → 跳問第 51 題，故第 50 題限(2)(3)
    //     選第4項「(90)身障者本人無法回答」→ 第 50、51 題都不顯示
    q156: homeServiceSection + ' and ({q155} = 2 or {q155} = 3)',
    q157: homeServiceSection + ' and ({q155} <> 4)',
    // 53. 次要收入來源選第15項「(95)無次要收入來源」→ 不問再次要收入來源
    q162: '{q161} <> 15',
    // 陸、工作狀況 章節標題：整章(60-62 題)都被隱藏時，標題也不顯示
    q173: workSection,
    // 60-62. 15歲以下、障礙類別為植物人者免填答，跳問第 63 題
    q174: workSection,
    // 60. (1)有在做工作 → 續問 60a，答完跳問第 62 題
    q175: workSection + ' and ({q174} = 1)',
    // 60. (2)無工作且在找工作 → 續問 60b
    q176: workSection + ' and ({q174} = 2)',
    // 60b. (2)不能立刻開始工作 → 續問原因，答完跳問第 63 題
    q177: workSection + ' and ({q174} = 2) and ({q176} = 2)',
    // 60. (4)沒有工作且未找工作 → 續問 60c，答完跳問第 63 題
    q178: workSection + ' and ({q174} = 4)',
    // 61. 60 題選(3)已找到工作，或選(2)且 60b 選(1)能立刻開始工作者才問
    q179:
      workSection + ' and (({q174} = 3) or (({q174} = 2) and ({q176} = 1)))',
    // 62. 只有 60 題選(1)有在做工作者作答
    q180: workSection + ' and ({q174} = 1)',
    // 63. (1)有做 → 63a；(2)沒有做過 → 63b；(98)不知道 → 跳問第 64 題
    q183: '{q182} = 1',
    q184: '{q182} = 2',
    // 64. 未滿25歲或75歲以上者免答，跳問第 65 題
    q185: isAge25To74,
    // 64. (1)有做 → 64a；(2)沒有做過 → 64b；(98)不知道 → 跳問第 65 題
    q186: isAge25To74 + ' and ({q185} = 1)',
    q187: isAge25To74 + ' and ({q185} = 2)',
    // 65. (1)需要定期就醫 → 續問 65a；(2)不需要 → 跳問第 66 題
    q189: '{q188} = 1',
    // 66. (1)需要復健治療 → 續問 66a、66b；(2)不需要 → 跳問第 67 題
    q191: '{q190} = 1',
    q192: '{q190} = 1',
    // 67. (2)診所 → 續問 67a
    q194: '{q193} = 2',
    // 68. (1)完全沒有困難 → 跳問第 69 題；(2)有困難 → 續問 68a、68b
    q196: '{q195} = 2',
    q197: '{q195} = 2',
    // 68bO. 68b 勾(6)缺乏與醫護人員溝通的支持服務 → 續問說明
    q198: '({q195} = 2) and ({q197} anyof [6])',
    // 68c. 68b 勾(3)醫院機構內缺乏無障礙環境 → 續問缺乏項目
    q199: '({q195} = 2) and ({q197} anyof [3])',
    // 70. (1)需要且有使用 → 70a；(2)需要但未使用 → 70b、70c
    q202: '{q201} = 1',
    q203: '{q201} = 2',
    q204: '{q201} = 2',
    // 72. (4)不好或(5)很不好 → 續問 72a
    q207: '{q206} = 4 or {q206} = 5',
    // 捌、休閒、社會參與及自我決定 章節標題：整章(73-82 題)都被隱藏時，標題也不顯示
    q208: notPlantPerson,
    // 73 題以後：障礙類別為植物人者於第 72 題後結束訪查
    q209: notPlantPerson,
    q210: notPlantPerson,
    // 73-2a~73-2d. 73-2 該列選「(4)無」(第 2 欄) → 續問該活動不想參與的主要原因
    q211: '(' + notPlantPerson + ') and ({q210.1} = 2)',
    q212: '(' + notPlantPerson + ') and ({q210.2} = 2)',
    q213: '(' + notPlantPerson + ') and ({q210.3} = 2)',
    q214: '(' + notPlantPerson + ') and ({q210.4} = 2)',
    // 74. 73 題完全沒有參與休閒活動者不用回答
    q215: '(' + notPlantPerson + ') and (' + joinedAnyLeisure + ')',
    // 74a. (2)有遭遇困難 → 續問哪些困難
    q216:
      '(' +
      notPlantPerson +
      ') and (' +
      joinedAnyLeisure +
      ') and ({q215} = 2)',
    q217: notPlantPerson,
    q218: notPlantPerson,
    // 75-2a~75-2d. 75-2 該列選「(2)沒有」→ 續問該社團不想參與的主要原因
    q219: '(' + notPlantPerson + ') and ({q218.1} = 2)',
    q220: '(' + notPlantPerson + ') and ({q218.2} = 2)',
    q221: '(' + notPlantPerson + ') and ({q218.3} = 2)',
    q222: '(' + notPlantPerson + ') and ({q218.4} = 2)',
    // 76. 限民國93年1月13日前(含當日)出生者作答
    q223: '(' + notPlantPerson + ') and ' + bornBeforeElectionDay,
    // 76. (2)沒有去投票 → 續問 76a
    q224: '(' + notPlantPerson + ') and ({q223} = 2)',
    // 77. (3)沒有投票權者跳問第 78 題
    q225: '(' + notPlantPerson + ') and ({q223} = 1 or {q223} = 2)',
    // 78. 限12歲及以上作答
    q226: '(' + notPlantPerson + ') and (' + isAge12AndUp + ')',
    q227: notPlantPerson,
    q228: notPlantPerson,
    q229: notPlantPerson,
    // 82. (2)不願意回答 → 82a-82c 皆不顯示，問卷自然結束
    q230: notPlantPerson,
    // 82a. 僅調查女性(第 1 題選(2)女)，其餘直接跳答 82b
    q231: '(' + notPlantPerson + ') and ({q230} = 1) and ({q58} = 2)',
    // 82b. 男性直接作答；女性限 82a 選(2)沒有（選(1)有則跳 82c）
    q232:
      '(' +
      notPlantPerson +
      ') and ({q230} = 1) and (({q58} <> 2) or ({q231} = 2))',
    // 82c. 82a 或 82b 任一選(1)有
    q233:
      '(' +
      notPlantPerson +
      ') and ({q230} = 1) and (({q231} = 1) or ({q232} = 1))',
    // F. (3)大部分非身心障礙者本人回答 → 續問 F1~F3
    // 訪員填寫欄位不受「植物人結束訪查」影響，一律作答
    q235: '{q234} = 3',
    q236: '{q234} = 3',
    q237: '{q234} = 3',
  })
  // 章節標題頁（壹~捌）無選項，以及訪員填寫欄位，一律非必答
  const notRequired = convertNotRequired([
    53, // D2.聯絡電話
    54, // D2.手機號碼
    57, // 壹、基本資料
    80, // 貳、居住狀況
    101, // 28.滿不滿意現在的居住狀況（限身心障礙者本人回答）
    102, // 29.期待的居住安排（限身心障礙者本人回答）
    103, // 參、外出及交通狀況
    125, // 肆、起居生活狀況
    158, // 伍、經濟狀況
    173, // 陸、工作狀況
    181, // 柒、健康及醫療照顧
    208, // 捌、休閒、社會參與及自我決定
    226, // 78.是否可以自己決定下列事項（限身心障礙者本人回答）
    238, // 意見欄
    239, // 可接受複查時段
  ])
  const mappedRateChoice = null
  const pageBreakQuestions = null
  const triggersConditionsInit = null
  // 帶出來源(前題) → 帶入目標(後題)：後題選項＝前題已勾選的選項
  const questionMappings = convertQuestionMappings({
    q78: 'q79', // 12.溝通方式(複選) → 12a.以上最主要溝通方式
    q130: 'q132', // 41.提供協助或照顧的人(複選) → 41a1.其中最主要的照顧者
  })

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 本案為政府委託訪問調查，結語採客戶指定文案，不使用 EZChoice 預設結語
  const endSurveyMsg = (survey) => {
    setCompletionMessage(`<div>問卷已成功送出，請致贈受訪者禮券，謝謝。</div>`)
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving:
      requiresLogin.login || webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
